"use client";

import { useEffect } from "react";
import {
  isEmptyMediaSlot,
  isImageRef,
  isSanityDocumentImage,
  stripPreviewBasePath,
  unwrapOptimizedImage,
  type PublisherInboundMessage,
  type PublisherOutboundMessage,
} from "./publisher-nav-protocol";
import { createPreviewSwaps, PREVIEW_ATTR } from "./publisher-asset-preview";

const OVERLAY_ATTR = "data-publisher-asset-overlay";
const SHIMMER_ATTR = "data-publisher-asset-shimmer";
const ERROR_ATTR = "data-publisher-asset-error";
const HIGHLIGHT_ATTR = "data-publisher-asset-highlight";
const ERROR_RED = "#c4321b";
// Rescanning every frame would query every img 60 times a second.
const ERROR_RESCAN_FRAMES = 30;
const SAMPLE_POINTS = [
  [0.5, 0.5],
  [0.1, 0.1],
  [0.9, 0.1],
  [0.1, 0.9],
  [0.9, 0.9],
] as const;

// Inside <picture> the shown image comes from a <source>, not the img's src.
function srcOf(img: HTMLImageElement): string | null {
  let raw = img.getAttribute("src");
  if (img.parentElement instanceof HTMLPictureElement && img.currentSrc) {
    const url = new URL(img.currentSrc);
    raw =
      url.origin === window.location.origin
        ? url.pathname + url.search
        : url.href;
  }
  return raw === null ? null : unwrapOptimizedImage(stripPreviewBasePath(raw));
}

// The pages mark art direction (backdrops, patterns, illustrations) as decorative, never content.
function isDecorative(img: HTMLImageElement): boolean {
  return (
    img.getAttribute("alt")?.trim() === "" ||
    img.closest('[aria-hidden="true"]') !== null
  );
}

// A Sanity draft offers only its document's images; a code page never offers them.
function editableImage(
  target: EventTarget | null,
  sanity: boolean,
): HTMLImageElement | null {
  if (!(target instanceof Element)) return null;
  const img = target.closest("img");
  const src = img ? srcOf(img) : null;
  if (!img || !isImageRef(src) || isSanityDocumentImage(src) !== sanity)
    return null;
  if (img.closest('[data-publisher-asset="locked"]')) return null;
  if (isDecorative(img) && !isEmptyMediaSlot(src)) return null;
  // Its code doesn't have this image until the save lands.
  if (img.hasAttribute(PREVIEW_ATTR)) return null;
  return img;
}

const LABEL_MAX = 80;

// Names the image in the changelog: its alt, else the closest heading around it.
function labelOf(img: HTMLImageElement): string | undefined {
  const clean = (text: string | null | undefined) =>
    text?.replace(/\s+/g, " ").trim().slice(0, LABEL_MAX) || undefined;
  const alt = clean(img.getAttribute("alt"));
  if (alt) return alt;
  for (
    let el = img.parentElement;
    el && el !== document.body;
    el = el.parentElement
  ) {
    const heading = clean(
      el.querySelector("h1, h2, h3, h4, h5, h6")?.textContent,
    );
    if (heading) return heading;
  }
  return undefined;
}

// A carousel can move or cover the image while the overlay is still showing.
function stillShowing(img: HTMLImageElement, src: string): boolean {
  if (!img.isConnected || srcOf(img) !== src) return false;
  const rect = img.getBoundingClientRect();
  // A centred control can sit over the middle, so any uncovered sample point counts.
  return SAMPLE_POINTS.some(([fx, fy]) => {
    const top = document
      .elementsFromPoint(
        rect.left + rect.width * fx,
        rect.top + rect.height * fy,
      )
      .find((el) => !el.closest(`[${OVERLAY_ATTR}]`));
    return top === img;
  });
}

// A sticky header over the image counts as covering it; the image's own Play control doesn't.
function floatsOver(el: Element | null, img: HTMLImageElement): boolean {
  for (let n = el; n && !n.contains(img); n = n.parentElement) {
    const { position } = getComputedStyle(n);
    if (position === "fixed" || position === "sticky") return true;
  }
  return false;
}

function overlayButton(
  label: string,
  ready: boolean,
  onClick: () => void,
): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.textContent = label;
  button.disabled = !ready;
  Object.assign(button.style, {
    font: "400 14px/1.4 system-ui, sans-serif",
    padding: "5px 10px",
    border: "0",
    borderRadius: "4px",
    background: "#ff5600",
    color: "#111",
    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
    cursor: ready ? "pointer" : "default",
    opacity: ready ? "1" : "0.5",
  });
  button.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    onClick();
  });
  return button;
}

function imagesFor(src: string): HTMLImageElement[] {
  return Array.from(document.querySelectorAll("img")).filter(
    (img) => srcOf(img) === src,
  );
}

export function PublisherAssetBridge({
  parentOrigin = "*",
}: {
  parentOrigin?: string;
} = {}): null {
  useEffect(() => {
    if (window.parent === window) return;
    const post = (message: PublisherOutboundMessage) =>
      window.parent.postMessage(message, parentOrigin);
    let enabled = false;
    let sanity = false;
    let undoable = new Set<string>();
    let undoReady = true;
    let overlay: HTMLDivElement | null = null;
    let current: HTMLImageElement | null = null;
    let pointer: { x: number; y: number } | null = null;
    let frame = 0;
    let refreshFrame = 0;

    const removeOverlay = () => {
      cancelAnimationFrame(frame);
      overlay?.remove();
      overlay = null;
      current = null;
    };

    const placeOverlay = (img: HTMLImageElement) => {
      removeOverlay();
      const src = srcOf(img);
      if (src === null) return;
      const el = document.createElement("div");
      const follow = () => {
        const rect = img.getBoundingClientRect();
        Object.assign(el.style, {
          left: `${rect.left}px`,
          top: `${rect.top}px`,
          width: `${rect.width}px`,
          height: `${rect.height}px`,
        });
        return rect;
      };
      // Follows the image: dropping it on a scroll or hover-scale left nothing to re-show it.
      const watch = () => {
        const rect = follow();
        const off =
          pointer &&
          (pointer.x < rect.left ||
            pointer.x > rect.right ||
            pointer.y < rect.top ||
            pointer.y > rect.bottom ||
            floatsOver(underPointer(), img));
        if (off || !stillShowing(img, src)) {
          removeOverlay();
          return scheduleRefresh();
        }
        frame = requestAnimationFrame(watch);
      };
      overlay = el;
      overlay.setAttribute(OVERLAY_ATTR, "");
      follow();
      Object.assign(overlay.style, {
        position: "fixed",
        outline: "2px solid #ff5600",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: "2147483647",
      });
      // Undo stands alone once replaced: a small image has no room for two buttons.
      overlay.appendChild(
        undoable.has(src)
          ? overlayButton("Undo", undoReady, () => {
              const showing = stillShowing(img, src);
              removeOverlay();
              if (!showing) return;
              post({
                type: "publisher:asset-undo",
                path: window.location.pathname,
                src,
              });
            })
          : overlayButton(
              isEmptyMediaSlot(src) ? "Add image" : "Replace image",
              true,
              () => {
                if (!stillShowing(img, src)) return removeOverlay();
                post({
                  type: "publisher:asset-select",
                  path: window.location.pathname,
                  src,
                  label: labelOf(img),
                });
              },
            ),
      );
      overlay.addEventListener("mouseleave", removeOverlay);
      document.body.appendChild(overlay);
      current = img;
      frame = requestAnimationFrame(watch);
    };

    function underPointer(): Element | null {
      if (!pointer) return null;
      return (
        document
          .elementsFromPoint(pointer.x, pointer.y)
          .find((el) => !el.closest(`[${OVERLAY_ATTR}]`)) ?? null
      );
    }

    const refresh = () => {
      if (!enabled || !pointer) return;
      const img = editableImage(underPointer(), sanity);
      if (img && img !== current) placeOverlay(img);
    };

    function scheduleRefresh() {
      cancelAnimationFrame(refreshFrame);
      refreshFrame = requestAnimationFrame(refresh);
    }

    const onMove = (event: MouseEvent) => {
      pointer = { x: event.clientX, y: event.clientY };
      scheduleRefresh();
    };
    const onLeave = () => {
      pointer = null;
      removeOverlay();
    };

    const shimmers = new Map<HTMLImageElement, HTMLElement>();
    let shimmerSrcs: string[] = [];
    let shimmerFrame = 0;
    // Previewed swaps shimmer too, until the saved image replaces them.
    const trackShimmers = () => {
      cancelAnimationFrame(shimmerFrame);
      const targets = new Set([
        ...document.querySelectorAll<HTMLImageElement>(`img[${PREVIEW_ATTR}]`),
        ...shimmerSrcs.flatMap(imagesFor),
      ]);
      for (const [img, node] of shimmers) {
        if (targets.has(img)) continue;
        node.remove();
        shimmers.delete(img);
      }
      for (const img of targets) {
        let node = shimmers.get(img);
        if (!node) {
          node = document.createElement("div");
          node.setAttribute(SHIMMER_ATTR, "");
          document.body.appendChild(node);
          shimmers.set(img, node);
        }
        const rect = img.getBoundingClientRect();
        Object.assign(node.style, {
          display: rect.width ? "block" : "none",
          left: `${rect.left}px`,
          top: `${rect.top}px`,
          width: `${rect.width}px`,
          height: `${rect.height}px`,
        });
      }
      if (shimmers.size) shimmerFrame = requestAnimationFrame(trackShimmers);
    };
    const setShimmer = (srcs: string[]) => {
      shimmerSrcs = srcs;
      trackShimmers();
    };

    let errored: string[] = [];
    let errorFrame = 0;
    let errorTick = 0;
    const errorBoxes = new Map<HTMLImageElement, HTMLDivElement>();
    const errorBox = () => {
      const box = document.createElement("div");
      box.setAttribute(ERROR_ATTR, "");
      Object.assign(box.style, {
        position: "fixed",
        outline: `2px solid ${ERROR_RED}`,
        background: "rgba(196,50,27,0.2)",
        pointerEvents: "none",
        zIndex: "2147483646",
      });
      const label = document.createElement("span");
      label.textContent = "Couldn't replace this image";
      Object.assign(label.style, {
        position: "absolute",
        top: "8px",
        left: "8px",
        font: "500 13px/1 system-ui, sans-serif",
        padding: "6px 8px",
        borderRadius: "4px",
        background: ERROR_RED,
        color: "#fff",
      });
      box.appendChild(label);
      return box;
    };
    const paintErrors = () => {
      cancelAnimationFrame(errorFrame);
      if (errorTick++ % ERROR_RESCAN_FRAMES === 0) {
        const shown = new Set(
          errored.flatMap((src) =>
            imagesFor(src).filter((el) => el.offsetParent !== null),
          ),
        );
        for (const [img, box] of errorBoxes) {
          if (shown.has(img)) continue;
          box.remove();
          errorBoxes.delete(img);
        }
        for (const img of shown) {
          if (errorBoxes.has(img)) continue;
          const box = errorBox();
          document.body.appendChild(box);
          errorBoxes.set(img, box);
        }
      }
      for (const [img, box] of errorBoxes) {
        const rect = img.getBoundingClientRect();
        Object.assign(box.style, {
          left: `${rect.left}px`,
          top: `${rect.top}px`,
          width: `${rect.width}px`,
          height: `${rect.height}px`,
        });
      }
      if (errored.length) errorFrame = requestAnimationFrame(paintErrors);
    };
    const setErrors = (srcs: string[]) => {
      errored = srcs;
      errorTick = 0;
      paintErrors();
    };

    let highlighted = new Set<string>();
    const syncHighlights = () => {
      for (const img of document.querySelectorAll("img")) {
        const src = srcOf(img);
        img.toggleAttribute(
          HIGHLIGHT_ATTR,
          src !== null && highlighted.has(src),
        );
      }
    };

    // Matches the page builder's section placeholder: fin tint and outline, white sweep.
    const style = document.createElement("style");
    style.textContent = `
img[${HIGHLIGHT_ATTR}] {
  outline: 2px solid var(--color-fin, #ff5600) !important;
  outline-offset: -2px !important;
}
[${SHIMMER_ATTR}] {
  position: fixed;
  z-index: 2147483646;
  pointer-events: none;
  border-radius: 7px;
  outline: 2px solid #ff5600;
  outline-offset: -2px;
  background-color: color-mix(in oklab, #ff5600 10%, transparent);
  background-image: linear-gradient(110deg, transparent 0%, rgba(255,255,255,0.35) 30%, rgba(255,255,255,0.85) 50%, rgba(255,255,255,0.35) 70%, transparent 100%);
  background-size: 680px 100%;
  background-repeat: repeat;
  animation: publisher-asset-shimmer 2.5s linear infinite;
}
@keyframes publisher-asset-shimmer {
  from { background-position: 0 0; }
  to { background-position: 680px 0; }
}
@media (prefers-reduced-motion: reduce) {
  [${SHIMMER_ATTR}] { animation: none; }
}`;
    document.head.appendChild(style);

    const onMessage = (event: MessageEvent) => {
      if (event.source !== window.parent) return;
      if (parentOrigin !== "*" && event.origin !== parentOrigin) return;
      const data = event.data as PublisherInboundMessage | undefined;
      if (!data || typeof data !== "object") return;
      if (data.type === "publisher:asset-enable") {
        enabled = data.enabled === true;
        sanity = data.sanity === true;
        if (enabled) scheduleRefresh();
        else removeOverlay();
      } else if (data.type === "publisher:asset-shimmer") {
        setShimmer(
          Array.isArray(data.srcs) ? data.srcs.filter(isImageRef) : [],
        );
      } else if (data.type === "publisher:asset-error") {
        setErrors(Array.isArray(data.srcs) ? data.srcs.filter(isImageRef) : []);
      } else if (
        data.type === "publisher:asset-reveal" &&
        isImageRef(data.src)
      ) {
        const img = imagesFor(data.src).find((el) => el.offsetParent !== null);
        img?.scrollIntoView({ block: "center", behavior: "smooth" });
      } else if (data.type === "publisher:asset-preview") {
        const swaps = Array.isArray(data.swaps)
          ? data.swaps.filter(
              (s) => s && isImageRef(s.from) && isImageRef(s.to),
            )
          : [];
        previewing = swaps.length > 0;
        previews.set(swaps);
        trackShimmers();
        syncHighlights();
        removeOverlay();
        watchDom();
      } else if (data.type === "publisher:asset-highlight") {
        highlighted = new Set(
          Array.isArray(data.srcs) ? data.srcs.filter(isImageRef) : [],
        );
        syncHighlights();
        watchDom();
      } else if (data.type === "publisher:asset-undoable") {
        undoable = new Set(
          Array.isArray(data.srcs) ? data.srcs.filter(isImageRef) : [],
        );
        undoReady = data.ready !== false;
        // Rebuilt under the pointer, with or without Undo.
        removeOverlay();
        scheduleRefresh();
      }
    };

    const previews = createPreviewSwaps(srcOf);
    let previewFrame = 0;
    // Catches images mounted later (carousel slides) and React resetting a src.
    const observer = new MutationObserver(() => {
      cancelAnimationFrame(previewFrame);
      previewFrame = requestAnimationFrame(() => {
        previews.apply();
        trackShimmers();
        syncHighlights();
      });
    });
    const observed: MutationObserverInit = {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["src", "srcset"],
    };
    let previewing = false;
    function watchDom() {
      if (previewing || highlighted.size) {
        observer.observe(document.body, observed);
      } else observer.disconnect();
    }
    // A <picture> img's currentSrc changes on load without any mutation.
    const onLoad = (event: Event) => {
      if (highlighted.size && event.target instanceof HTMLImageElement) {
        syncHighlights();
      }
    };

    document.addEventListener("load", onLoad, true);
    document.addEventListener("mousemove", onMove, { passive: true });
    document.documentElement.addEventListener("mouseleave", onLeave);
    window.addEventListener("scroll", scheduleRefresh, { passive: true });
    window.addEventListener("message", onMessage);
    post({ type: "publisher:asset-ready" });
    return () => {
      document.removeEventListener("load", onLoad, true);
      document.removeEventListener("mousemove", onMove);
      document.documentElement.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("scroll", scheduleRefresh);
      window.removeEventListener("message", onMessage);
      removeOverlay();
      cancelAnimationFrame(refreshFrame);
      observer.disconnect();
      cancelAnimationFrame(previewFrame);
      cancelAnimationFrame(shimmerFrame);
      shimmers.forEach((node) => node.remove());
      setErrors([]);
      highlighted = new Set();
      syncHighlights();
      style.remove();
    };
  }, [parentOrigin]);
  return null;
}
